import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAPI } from "../../hooks/useAPI";
import { useAuth } from "../../contexts/AuthContext";
import { useSubscribedPrograms } from "../../hooks/useSubscribedPrograms";
import { Icons } from "../SkillsAcademy/icons.jsx";
import TopicSection from "./TopicSection";
import UnlockModal from "../UnlockModal/UnlockModal";
import ActionButton from "../ActionButton/ActionButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import {
  ProgramDetailRoot,
  ProgramDetailBackBtn,
  ProgramDetailHero,
  ProgramDetailPosterWrap,
  ProgramDetailPosterImg,
  ProgramDetailPosterTint,
  ProgramDetailPosterContent,
  ProgramDetailPillRow,
  ProgramDetailHeroTitle,
  ProgramDetailGuruRow,
  ProgramDetailGuruAvatar,
  ProgramDetailGuruText,
  ProgramDetailGuruName,
  ProgramDetailSidebar,
  ProgramDetailOverviewLabel,
  ProgramDetailBlurb,
  ProgramDetailStatsGrid,
  ProgramDetailStatItem,
  ProgramDetailStatIconRow,
  ProgramDetailStatLabel,
  ProgramDetailStatNum,
  ProgramDetailStatusBanner,
  ProgramDetailStatusMain,
  ProgramDetailStatusSub,
  ProgramDetailProgressSection,
  ProgramDetailProgressLabel,
  ProgramDetailProgressHeading,
  ProgramDetailProgressPct,
  ProgramDetailProgressTrack,
  ProgramDetailProgressFill,
  ProgramDetailProgressSessionsRow,
  ProgramDetailProgressSessionChip,
  ProgramDetailActions,
  ProgramDetailBtnSecondary,
  ProgramDetailTopicsSection,
  ProgramDetailTopicsHeader,
  ProgramDetailTopicsTitle,
  ProgramDetailTopicsList,
  ProgramDetailUpgradeStrip,
  ProgramDetailUpgradeText,
  ProgramDetailUpgradeTitle,
  ProgramDetailUpgradeSubtitle,
  DetailPill,
} from "./style.js";
import { AccordionRoot } from "../SkillsAcademy/style.js";

// ── Types ─────────────────────────────────────────────────────────────────────

type TopicStatus = "completed" | "in-progress" | "up-next" | "locked";
type SessionStatus = "completed" | "next" | "available" | "locked";

// ── Component ─────────────────────────────────────────────────────────────────

const ProgramDetail = () => {
  const { trainingprogram_id } = useParams<{ trainingprogram_id: string }>();
  const navigate = useNavigate();
  const { callAPI } = useAPI();
  const { authToken, loggedInUser } = useAuth();
  const isTester = !!(loggedInUser as { is_tester?: boolean })?.is_tester;
  const is2FA = window.location.pathname.toLowerCase().includes("2fa");

  const { data: trainingProgram } = useQuery({
    queryKey: ["trainingProgram", trainingprogram_id],
    queryFn: () =>
      callAPI(`trainingprograms?trainingprogram_id=${trainingprogram_id}`, {
        token: authToken,
      }),
    enabled: !!authToken && !!trainingprogram_id && !is2FA,
    staleTime: 0,
  });

  const { data: enrollments = [] } = useSubscribedPrograms();
  const [unlockOpen, setUnlockOpen] = useState(false);

  const enrollment = enrollments.find((e: any) => e.programId === trainingprogram_id) ?? null;
  const isTrial = enrollment?.type === "trial";
  const completed = enrollment?.completed ?? false;
  const overallProgress = enrollment?.overallProgress ?? 0;
  const sessionsCompleted = enrollment?.sessionsCompleted ?? 0;
  const simsCompleted = enrollment?.simulationsCompleted ?? 0;
  const daysLeftInTrial = enrollment?.daysLeftInTrial;

  // ── Transform chapters → topics format ────────────────────────────────────

  const allScenarios = useMemo(
    () => (trainingProgram?.chapters ?? []).flatMap((c: any) => c.scenarios ?? []),
    [trainingProgram]
  );

  const lessonCount = allScenarios.filter((s: any) => !s.has_scoring_engine).length;
  const simCount = allScenarios.filter((s: any) => s.has_scoring_engine).length;
  const sessionsTotal = enrollment?.sessionsTotal ?? lessonCount;
  const simsTotal = enrollment?.simulationsTotal ?? simCount;

  const topics = useMemo(
    () =>
      (trainingProgram?.chapters ?? []).map((chapter: any) => ({
        name: chapter.name,
        desc: chapter.description,
        sessions: (chapter.scenarios ?? []).map((s: any) => ({
          id: s.id,
          type: s.has_scoring_engine ? "sim" : "skill",
          title: s.name,
        })),
      })),
    [trainingProgram]
  );

  // ── Expand/collapse state (one entry per chapter) ─────────────────────────

  const [openStates, setOpenStates] = useState<boolean[]>(() =>
    (trainingProgram?.chapters ?? []).map((_: any, i: number) => i === 0)
  );

  // Re-sync if chapters load after initial render
  const chapterCount = trainingProgram?.chapters?.length ?? 0;
  const syncedOpenStates = openStates.length === chapterCount
    ? openStates
    : (trainingProgram?.chapters ?? []).map((_: any, i: number) => i === 0);

  const allExpanded = syncedOpenStates.length > 0 && syncedOpenStates.every(Boolean);
  const handleToggleAll = () => setOpenStates(syncedOpenStates.map(() => !allExpanded));
  const handleToggleOne = (i: number) =>
    setOpenStates(syncedOpenStates.map((v: boolean, idx: number) => (idx === i ? !v : v)));

  // ── Compute sequential lock state (API doesn't return locked) ────────────
  // Chapter locked if previous chapter not fully completed.
  // Scenario locked if chapter is locked OR previous scenario not completed.

  const lockedByScenarioId: Record<string, boolean> = useMemo(() => {
    const map: Record<string, boolean> = {};
    const chapters = trainingProgram?.chapters ?? [];
    let prevChapterCompleted = true;
    for (const chapter of chapters) {
      const chapterLocked = !prevChapterCompleted;
      let prevScenarioCompleted = true;
      for (const s of chapter.scenarios ?? []) {
        // Testers can run any scenario in any order — nothing is ever locked.
        map[s.id] = isTester ? false : chapterLocked || !prevScenarioCompleted;
        prevScenarioCompleted = !!s.completed;
      }
      prevChapterCompleted = !!(chapter.completed);
    }
    return map;
  }, [trainingProgram, isTester]);

  // ── Session states from real completion + lock data ───────────────────────

  const sessionStatesByTopic: SessionStatus[][] = useMemo(() => {
    let nextAssigned = false;
    return (trainingProgram?.chapters ?? []).map((chapter: any) =>
      (chapter.scenarios ?? []).map((s: any): SessionStatus => {
        if (lockedByScenarioId[s.id]) return "locked";
        if (s.completed) return "completed";
        if (!nextAssigned) { nextAssigned = true; return "next"; }
        // Testers can start any scenario; others see not-yet-reached as locked.
        return isTester ? "available" : "locked";
      })
    );
  }, [trainingProgram, lockedByScenarioId, isTester]);

  // ── Topic statuses from real chapter state ─────────────────────────────────

  const topicStatuses: TopicStatus[] = useMemo(() => {
    let firstInProgress = false;
    return (trainingProgram?.chapters ?? []).map((chapter: any, i: number): TopicStatus => {
      const scenarios = chapter.scenarios ?? [];
      const allLocked = scenarios.every((s: any) => lockedByScenarioId[s.id]);
      if (allLocked && i > 0) return "locked";
      const isCompleted = chapter.completed || (scenarios.length > 0 && scenarios.every((s: any) => s.completed));
      if (isCompleted) return "completed";
      if (!firstInProgress) { firstInProgress = true; return "in-progress"; }
      return "up-next";
    });
  }, [trainingProgram, lockedByScenarioId]);

  // ── Next unlocked scenario for Continue button ─────────────────────────────

  const nextScenario = useMemo(() => {
    for (const chapter of trainingProgram?.chapters ?? []) {
      for (const s of chapter.scenarios ?? []) {
        if (!s.completed && !lockedByScenarioId[s.id]) return s;
      }
    }
    return null;
  }, [trainingProgram, lockedByScenarioId]);

  const accent = (trainingProgram?.accent as string[] | null) ?? ["#01ECFF", "#962EFF"];
  const guruInfo = trainingProgram?.guru_info;
  const statusType = completed ? "completed" : isTrial ? "trial" : "active";

  // ── Loading state ──────────────────────────────────────────────────────────

  if (!trainingProgram) {
    return (
      <ProgramDetailRoot>
        <ProgramDetailBackBtn type="button" onClick={() => navigate("/dashboard/my-programs")}>
          <Icons.arrowLeft size={16} />
          Back to My Programs
        </ProgramDetailBackBtn>
        <div style={{ padding: "60px 40px", textAlign: "center", color: "#D1C7FF" }}>
          Loading programme…
        </div>
      </ProgramDetailRoot>
    );
  }

  return (
    <ProgramDetailRoot>
      <ProgramDetailBackBtn type="button" onClick={() => navigate("/dashboard/my-programs")}>
        <Icons.arrowLeft size={16} />
        Back to My Programs
      </ProgramDetailBackBtn>

      {/* ── Hero ── */}
      <ProgramDetailHero>
        {/* Poster */}
        <ProgramDetailPosterWrap>
          <ProgramDetailPosterImg $src={trainingProgram.image} />
          <ProgramDetailPosterTint $a={accent[0]} $b={accent[1]} />
          <ProgramDetailPosterContent>
            <ProgramDetailPillRow>
              {isTrial && (
                <DetailPill
                  $bg="linear-gradient(95deg, rgba(1,236,255,0.90), rgba(150,46,255,0.90))"
                  $fg="#030039"
                  $bd="transparent"
                >
                  <Icons.zap size={12} /> Free Trial
                </DetailPill>
              )}
              {trainingProgram.category && (
                <DetailPill
                  $bg="rgba(0,0,0,0.55)"
                  $fg="#D1C7FF"
                  $bd="rgba(255,255,255,0.18)"
                >
                  {trainingProgram.category}
                </DetailPill>
              )}
            </ProgramDetailPillRow>
            <ProgramDetailHeroTitle>{trainingProgram.name}</ProgramDetailHeroTitle>
            {guruInfo && (
              <ProgramDetailGuruRow>
                <ProgramDetailGuruAvatar $src={guruInfo.photo ?? ""} />
                <ProgramDetailGuruText>
                  A Program By{" "}
                  <ProgramDetailGuruName>{guruInfo.full_name}</ProgramDetailGuruName>
                  {guruInfo.role && ` · ${guruInfo.role}`}
                </ProgramDetailGuruText>
              </ProgramDetailGuruRow>
            )}
          </ProgramDetailPosterContent>
        </ProgramDetailPosterWrap>

        {/* Sidebar */}
        <ProgramDetailSidebar>
          <div>
            <ProgramDetailOverviewLabel>Program Overview</ProgramDetailOverviewLabel>
            <ProgramDetailBlurb>{trainingProgram.description}</ProgramDetailBlurb>
          </div>

          {/* Stats */}
          <ProgramDetailStatsGrid>
            <ProgramDetailStatItem>
              <ProgramDetailStatIconRow>
                <Icons.flame size={12} style={{ color: "#A87EFF" }} />
                <ProgramDetailStatLabel>Difficulty Level</ProgramDetailStatLabel>
              </ProgramDetailStatIconRow>
              <ProgramDetailStatNum>
                {trainingProgram.difficulty_level
                  ? trainingProgram.difficulty_level.charAt(0).toUpperCase() +
                    trainingProgram.difficulty_level.slice(1)
                  : "—"}
              </ProgramDetailStatNum>
            </ProgramDetailStatItem>

            <ProgramDetailStatItem>
              <ProgramDetailStatIconRow>
                <Icons.topic size={12} style={{ color: "#A87EFF" }} />
                <ProgramDetailStatLabel>Topics</ProgramDetailStatLabel>
              </ProgramDetailStatIconRow>
              <ProgramDetailStatNum>{topics.length}</ProgramDetailStatNum>
            </ProgramDetailStatItem>

            <ProgramDetailStatItem>
              <ProgramDetailStatIconRow>
                <Icons.book size={12} style={{ color: "#A87EFF" }} />
                <ProgramDetailStatLabel>Skill Dev Sessions</ProgramDetailStatLabel>
              </ProgramDetailStatIconRow>
              <ProgramDetailStatNum>{lessonCount}</ProgramDetailStatNum>
            </ProgramDetailStatItem>

            <ProgramDetailStatItem>
              <ProgramDetailStatIconRow>
                <Icons.users size={12} style={{ color: "#A87EFF" }} />
                <ProgramDetailStatLabel>Simulations</ProgramDetailStatLabel>
              </ProgramDetailStatIconRow>
              <ProgramDetailStatNum>{simCount}</ProgramDetailStatNum>
            </ProgramDetailStatItem>
          </ProgramDetailStatsGrid>

          {/* Status banner */}
          <ProgramDetailStatusBanner $type={statusType}>
            {isTrial && <Icons.zap size={20} style={{ color: "#01ECFF", flexShrink: 0 }} />}
            {completed && <Icons.check size={20} style={{ color: "#17B26A", flexShrink: 0 }} />}
            <div>
              <ProgramDetailStatusMain $type={statusType}>
                {completed ? "Program completed" : isTrial ? "Free trial active" : "Enrolled"}
              </ProgramDetailStatusMain>
              {isTrial && daysLeftInTrial != null && (
                <ProgramDetailStatusSub>
                  · {daysLeftInTrial} day{daysLeftInTrial !== 1 ? "s" : ""} remaining
                </ProgramDetailStatusSub>
              )}
            </div>
          </ProgramDetailStatusBanner>

          {/* Progress */}
          <ProgramDetailProgressSection>
            <ProgramDetailProgressLabel>
              <ProgramDetailProgressHeading>Program Progress</ProgramDetailProgressHeading>
              <ProgramDetailProgressPct $isCompleted={completed}>
                {overallProgress}% completed
              </ProgramDetailProgressPct>
            </ProgramDetailProgressLabel>
            <ProgramDetailProgressTrack>
              <ProgramDetailProgressFill $pct={overallProgress} $isCompleted={completed} />
            </ProgramDetailProgressTrack>
            <ProgramDetailProgressSessionsRow>
              <ProgramDetailProgressSessionChip>
                <Icons.book size={12} style={{ color: "#A87EFF" }} />
                {sessionsCompleted} / {sessionsTotal} Skill Dev Sessions
              </ProgramDetailProgressSessionChip>
              <ProgramDetailProgressSessionChip>
                <Icons.users size={12} style={{ color: "#A87EFF" }} />
                {simsCompleted} / {simsTotal} Simulation Challenges
              </ProgramDetailProgressSessionChip>
            </ProgramDetailProgressSessionsRow>
          </ProgramDetailProgressSection>

          {/* Actions */}
          <ProgramDetailActions>
            <ActionButton
              variant="primary"
              size="md"
              fullWidth
              icon={<Icons.play size={14} />}
              type="button"
              disabled={!nextScenario}
              onClick={() => nextScenario && navigate(`/dashboard/simulate/${nextScenario.id}`)}
            >
              {overallProgress === 0 ? "Start session" : completed ? "Replay" : "Continue"}
            </ActionButton>
            {isTrial && (
              <ActionButton
                variant="subtle"
                size="md"
                fullWidth
                icon={<Icons.zap size={14} />}
                type="button"
                onClick={() => setUnlockOpen(true)}
              >
                Unlock full program
              </ActionButton>
            )}
          </ProgramDetailActions>
        </ProgramDetailSidebar>
      </ProgramDetailHero>

      {/* ── Topics ── */}
      <ProgramDetailTopicsSection>
        <ProgramDetailTopicsHeader>
          <ProgramDetailTopicsTitle>Program Topics</ProgramDetailTopicsTitle>
          <ActionButton
            variant="subtle"
            size="sm"
            type="button"
            icon={allExpanded ? <RemoveIcon sx={{ fontSize: 14 }} /> : <AddIcon sx={{ fontSize: 16 }} />}
            onClick={handleToggleAll}
          >
            {allExpanded ? "Collapse all" : "Expand all"}
          </ActionButton>
        </ProgramDetailTopicsHeader>
        <ProgramDetailTopicsList>
          <AccordionRoot>
            {topics.map((topic: any, i: number) => (
              <TopicSection
                key={i}
                topic={topic}
                status={topicStatuses[i]}
                isOpen={syncedOpenStates[i] ?? false}
                onToggle={() => handleToggleOne(i)}
                isTrial={isTrial}
                topicIndex={i}
                sessionStatuses={sessionStatesByTopic[i]}
                onSessionClick={(scenarioId) => navigate(`/dashboard/simulate/${scenarioId}`)}
              />
            ))}
          </AccordionRoot>
        </ProgramDetailTopicsList>
      </ProgramDetailTopicsSection>

      {/* ── Upgrade strip — trial only ── */}
      {isTrial && !completed && (
        <ProgramDetailUpgradeStrip>
          <ProgramDetailUpgradeText>
            <ProgramDetailUpgradeTitle>Loving it so far?</ProgramDetailUpgradeTitle>
            <ProgramDetailUpgradeSubtitle>
              Unlock the full program to access all {topics.length} topics,{" "}
              {lessonCount} sessions and {simCount} simulations.
            </ProgramDetailUpgradeSubtitle>
          </ProgramDetailUpgradeText>
          <ActionButton
            variant="primary"
            size="lg"
            icon={<Icons.zap size={18} />}
            type="button"
            onClick={() => setUnlockOpen(true)}
          >
            Unlock full program
          </ActionButton>
        </ProgramDetailUpgradeStrip>
      )}

      <UnlockModal
        open={unlockOpen}
        onClose={() => setUnlockOpen(false)}
        programId={trainingProgram.id}
      />
    </ProgramDetailRoot>
  );
};

export default ProgramDetail;
